
import { useState, useEffect } from "react";

import { Search as SearchIcon } from "lucide-react";
import { BookCard } from "@/components/BookCard";
import { BookDialog } from "@/components/BookDialog";
import { useNavigate, useLocation } from "react-router-dom";
import { Book } from "@/types/book";
import { useBundles } from "@/hooks/use-bundles";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useCachedSearch } from "@/hooks/use-cached-search";
import { useBatchIndividualAccess } from "@/hooks/use-batch-individual-access";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";

export default function Search() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const { searchResults, isLoading } = useCachedSearch(query);
  const { session } = useAuth();
  
  const {
    activeBundle,
    permanentBundles,
    expiredBundles,
    hasActiveAccess,
    permanentBundleAccess,
    expiredBundleAccess,
  } = useBundles();

  // Get subscription status
  const { data: subscriptionStatus } = useSubscriptionStatus();

  // Batch check individual book access
  const bookIds = searchResults.map(book => Number(book.id));
  const { data: individualAccessMap } = useBatchIndividualAccess(bookIds);

  // Check if user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ['isAdmin', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return false;
      
      const { data: accountData, error: accountError } = await supabase
        .from('user_accounts')
        .select('is_admin')
        .eq('user_id', session.user.id)
        .maybeSingle();
        
      if (!accountError && accountData) {
        return accountData.is_admin;
      }

      const { data: rpcData, error: rpcError } = await supabase.rpc('is_admin_new', {
        user_id: session.user.id
      });
      
      if (rpcError) {
        console.error('Error checking admin status:', rpcError);
        return false;
      }
      
      return rpcData ?? false;
    },
    enabled: !!session?.user?.id
  });
  
  // NOTE: Removed scroll-to-top for instant tab switching feel

  // Check for query parameter on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
    }
  }, [location.search]);
  
  // Filter out dev books - only show books assigned to bundles or marked as preview
  const filteredBooks = searchResults.filter(book => 
    book.bundle_id !== null || book.is_preview === true
  );

  const handleReadNow = (bookId: number) => {
    navigate(`/read/${bookId}`);
  };

  const handlePurchase = () => {
    console.log("Purchase flow initiated for book:", selectedBook?.title);
  };

  function getBundleForBook(book: Book) {
    if (book.bundle_id === activeBundle?.id) return activeBundle;
    return (
      permanentBundles.find(b => b.id === book.bundle_id) ||
      expiredBundles.find(b => b.id === book.bundle_id) ||
      null
    );
  }

  // Check if user has access to a specific book
  const checkBookAccess = (book: Book | null) => {
    if (!book) return false;

    // Check if it's a preview book first - everyone has access to preview books
    if (book.is_preview) return true;

    // Determine bundle and whether the book is upcoming
    const bundle = getBundleForBook(book);
    const isUpcoming = bundle?.status === 'upcoming';

    // Never allow reading upcoming books (until the bundle is released)
    if (isUpcoming) return false;

    // Check subscription access next
    if (subscriptionStatus?.subscribed && subscriptionStatus?.subscription_status === 'active') {
      return true;
    }

    // Admin check
    if (isAdmin) return true;

    // If book has no bundle_id, check individual access only
    if (!book.bundle_id) {
      if (session?.user?.id && individualAccessMap) {
        return individualAccessMap.get(Number(book.id)) || false;
      }
      return false;
    }

    // Check bundle access
    let hasBundleAccess = false;
    if (book.bundle_id === activeBundle?.id) {
      hasBundleAccess = hasActiveAccess;
    } else {
      const bundle = getBundleForBook(book);
      if (bundle?.bundle_type === 'permanent') {
        hasBundleAccess = !!permanentBundleAccess[book.bundle_id];
      } else {
        hasBundleAccess = !!expiredBundleAccess[book.bundle_id];
      }
    }

    // Check individual book access if no bundle access
    if (!hasBundleAccess && session?.user?.id && individualAccessMap) {
      return individualAccessMap.get(Number(book.id)) || false;
    }

    return hasBundleAccess;
  };

  return (
    <div className="min-h-screen app-background overflow-y-auto pt-safe" style={{ 
      WebkitOverflowScrolling: 'touch',
      overscrollBehaviorY: 'contain'
    }}>
      
      
      <div className="container mx-auto px-4 pt-24 pb-32 md:pb-20">
        <h1 className="text-3xl md:text-5xl font-display font-bold text-white mb-8 tracking-wider text-center">
          Search Books
        </h1>
        
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              className="w-full h-12 pl-12 pr-4 rounded-full bg-white/5 backdrop-blur-lg border border-white/10 text-white transition-all focus:outline-none focus:ring-2 focus:ring-amber-500/50 placeholder-white/40"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="absolute left-4 top-3.5 text-white/60">
              <SearchIcon className="w-5 h-5" />
            </div>
          </div>
          
          {/* Cinema Screen Glowing Line */}
          <div className="relative mt-8 mb-10">
            <div 
              className="h-[1px] w-full rounded-full bg-white/5 overflow-hidden"
              style={{
                borderRadius: "100% / 50%", 
                transform: "perspective(500px) rotateX(5deg)",
                boxShadow: 
                  "0 0 5px 1px rgba(217, 70, 239, 0.2), 0 0 10px 2px rgba(30, 174, 219, 0.1)",
                animation: "borderGlow 4s ease-in-out infinite alternate"
              }}
            >
              <div 
                className="w-full h-full"
                style={{
                  background: "linear-gradient(90deg, #D946EF, #1EAEDB, #D946EF)",
                  backgroundSize: "200% 100%",
                  animation: "gradientShift 3s linear infinite",
                  opacity: 0.5
                }}
              />
            </div>
            
            <div 
              className="absolute w-full h-6 -bottom-6" 
              style={{
                background: "radial-gradient(ellipse at center, rgba(217, 70, 239, 0.15) 0%, rgba(30, 174, 219, 0.08) 40%, transparent 70%)",
                pointerEvents: "none",
                filter: "blur(8px)",
              }}
            />
            <div 
              className="absolute inset-0 w-full h-full"
              style={{
                background: "radial-gradient(ellipse at center, transparent 30%, rgba(7, 12, 23, 0.9) 80%)",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
        
        {isLoading ? (
          <div className="text-center text-white/60">
            {query ? "Searching..." : "Loading books..."}
          </div>
        ) : (
          <>
            {filteredBooks.length === 0 ? (
              <div className="text-center text-white/60 py-12">
                <h3 className="text-xl font-medium mb-2">No books found</h3>
                <p>Try searching with different keywords</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4 md:gap-6 pb-24 md:pb-0">
                {filteredBooks.map((book: Book) => {
                  const bundle = getBundleForBook(book);
                  let tagType: 'current' | 'expired' | 'upcoming' | undefined;
                  
                  if (book.bundle_id === activeBundle?.id) tagType = 'current';
                  else if (bundle?.status === 'upcoming') tagType = 'upcoming';
                  else if (bundle?.status === 'expired') tagType = 'expired';
                  
                  return (
                    <div key={book.id} className="flex flex-col">
                      <BookCard 
                        title={book.title}
                        cover_image_url={book.cover_image_url || ''}
                        rating={book.rating}
                        year={typeof book.year === 'number' ? book.year.toString() : undefined}
                        onClick={() => setSelectedBook(book)}
                        hideRatings={true}
                        bundleStartDate={bundle?.start_date}
                        bookId={book.id}
                        cacheVersion={book.cache_version}
                        bundleTagType={tagType}
                        bundleId={book.bundle_id || undefined}
                        showNotifyMe={tagType === 'upcoming'}
                        tags={book.tags}
                      />
                      <div className="mt-2 px-1">
                        <h3 className="text-white text-xs font-medium truncate text-center">
                          {book.title}
                        </h3>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      
      {selectedBook && (
        <BookDialog 
          book={selectedBook}
          isOpen={!!selectedBook}
          onClose={() => setSelectedBook(null)}
          onReadNow={() => handleReadNow(selectedBook.id)}
          onPurchase={handlePurchase}
          hasAccess={checkBookAccess(selectedBook)}
          bundlePrice={activeBundle?.price}
          bundleId={selectedBook?.bundle_id}
          onBookClick={setSelectedBook}
        />
      )}
    </div>
  );
}
